import { logger } from "@/utils/logger";
import { MessagePart } from './types';
import { parseJSONMessage } from './parsers/jsonMessageParser';
import { parseMarkdownJSON } from './parsers/markdownJSONParser';
import { parseFormSubmissions } from './formSubmissionParser';
import { parseFormattedContent } from './formattedContentParser';
import { validateAndLogJSON, detectDuplicateKeys, removeDuplicateKeys } from '../jsonValidator';
import { sessionManager } from '../sessionManager';
import { normalizeAIResponse } from '../normalizeAIResponse';

export interface ParseMessageOptions {
  sessionId?: string;
  skipIntroAndClosing?: boolean; // Skip intro/closing when they were already streamed
}

export const parseMessageContent = async (content: string, options?: ParseMessageOptions): Promise<MessagePart[]> => {
  // CRITICAL: Capture sessionId synchronously at the TOP of the call stack
  // This ensures we have the sessionId before any async operations run
  const capturedSessionId = options?.sessionId || sessionManager.getCurrentSession()?.sessionId || 'unknown';
  const parseOptions = { sessionId: capturedSessionId, skipIntroAndClosing: options?.skipIntroAndClosing };
  
  // Safety check: ensure content is a string
  if (!content || typeof content !== 'string') {
    console.error('❌❌❌ parseMessageContent: Invalid content type:', typeof content);
    return [{ type: 'text', content: 'Message could not be displayed' }];
  }
  
  // Early return for simple text content to avoid expensive parsing
  const trimmedContent = content.trim();
  
  // Check if this looks like JSON (starts with { or [)
  const startsWithJSON = trimmedContent.startsWith('{') || trimmedContent.startsWith('[');
  
  // CRITICAL: Also check if content CONTAINS a Reply_Message JSON block anywhere
  // This handles cases where AI outputs text BEFORE the JSON block
  const containsReplyMessageJSON = trimmedContent.includes('{"Reply_Message"') || 
                                    trimmedContent.includes('{ "Reply_Message"') ||
                                    trimmedContent.includes('"Reply_Message":');
  
  const looksLikeJSON = startsWithJSON || containsReplyMessageJSON;
  
  if (!looksLikeJSON && 
      !trimmedContent.includes('```json') &&
      !trimmedContent.includes('FormType:') &&
      !trimmedContent.includes('FORM_SUBMISSION:')) {
    // Likely plain text - skip expensive parsing
    return [{ type: 'text', content: content }];
  }
  
  // NEW: If content has text BEFORE JSON, extract just the JSON portion
  if (containsReplyMessageJSON && !startsWithJSON) {
    logger.log('🔧 Content has text before JSON block - extracting JSON portion');
    
    // Find where the JSON block starts
    const jsonStartIndex = trimmedContent.indexOf('{"Reply_Message"');
    const jsonStartIndex2 = trimmedContent.indexOf('{ "Reply_Message"');
    const actualJsonStart = jsonStartIndex !== -1 ? jsonStartIndex : jsonStartIndex2;
    
    if (actualJsonStart > 0) {
      const jsonPortion = trimmedContent.substring(actualJsonStart);
      logger.log('🔧 Extracted JSON starting at position', actualJsonStart);
      
      // Try to parse just the JSON portion
      try {
        // Find the end of the JSON object using brace counting
        let braceCount = 0;
        let inString = false;
        let escapeNext = false;
        let jsonEndPos = 0;
        
        for (let i = 0; i < jsonPortion.length; i++) {
          const char = jsonPortion[i];
          
          if (escapeNext) {
            escapeNext = false;
            continue;
          }
          
          if (char === '\\') {
            escapeNext = true;
            continue;
          }
          
          if (char === '"') {
            inString = !inString;
            continue;
          }
          
          if (!inString) {
            if (char === '{') braceCount++;
            if (char === '}') {
              braceCount--;
              if (braceCount === 0) {
                jsonEndPos = i + 1;
                break;
              }
            }
          }
        }
        
        if (jsonEndPos > 0) {
          const cleanJsonContent = jsonPortion.substring(0, jsonEndPos);
          logger.log('✅ Extracted clean JSON block of', cleanJsonContent.length, 'chars');
          
          const jsonData = JSON.parse(cleanJsonContent);
          const result = await parseJSONMessage(jsonData, parseOptions);
          
          if (result.length > 0) {
            logger.log('✅ Successfully parsed extracted JSON, returning', result.length, 'parts');
            return result;
          }
        }
      } catch (extractError) {
        console.error('❌ Failed to extract/parse JSON from mixed content:', extractError);
        // Fall through to regular parsing attempts
      }
    }
  }
  
  // FIRST: Try to parse the entire content as JSON (for API responses)
  try {
    // Try to extract just the JSON if there's trailing content
    let jsonContent = trimmedContent;
    
    // If JSON.parse fails due to trailing content, try to extract just the valid JSON portion
    try {
      JSON.parse(trimmedContent);
    } catch (initialError) {
      if (initialError instanceof SyntaxError && initialError.message.includes('position')) {
        // Extract position from error message
        const posMatch = initialError.message.match(/position (\d+)/);
        if (posMatch) {
          const errorPos = parseInt(posMatch[1]);
          logger.log('🔧 JSON parse failed at position', errorPos, '- attempting to extract valid JSON portion');
          
          // Try to find the last complete JSON object by counting braces
          let braceCount = 0;
          let inString = false;
          let escapeNext = false;
          let lastValidPos = 0;
          
          for (let i = 0; i < trimmedContent.length; i++) {
            const char = trimmedContent[i];
            
            if (escapeNext) {
              escapeNext = false;
              continue;
            }
            
            if (char === '\\') {
              escapeNext = true;
              continue;
            }
            
            if (char === '"') {
              inString = !inString;
              continue;
            }
            
            if (!inString) {
              if (char === '{') braceCount++;
              if (char === '}') {
                braceCount--;
                if (braceCount === 0) {
                  lastValidPos = i + 1;
                  break;
                }
              }
            }
          }
          
          if (lastValidPos > 0) {
            jsonContent = trimmedContent.substring(0, lastValidPos);
            logger.log('✅ Extracted JSON up to position', lastValidPos);
          }
        }
      }
      // If we can't extract, we'll let the normal error handling below catch it
    }
    
    // CRITICAL: Validate JSON for duplicate keys BEFORE parsing
    const duplicateCheck = detectDuplicateKeys(jsonContent);
    if (duplicateCheck.hasDuplicates) {
      console.error('❌ DUPLICATE KEYS DETECTED IN JSON BEFORE PARSING!');
      console.error('Duplicate keys:', duplicateCheck.duplicateKeys);
      console.error('This means OpenAI\'s strict mode failed to prevent invalid JSON');
      
      // FIX: Remove duplicate keys by keeping only the LAST occurrence of each key
      try {
        const cleanedJSON = removeDuplicateKeys(jsonContent);
        logger.log('🔧 Cleaned JSON by removing duplicate keys, attempting to parse...');
        const jsonData = JSON.parse(cleanedJSON);
        logger.log('✅ messageParserOrchestrator: JSON.parse successful after cleaning duplicates');
        logger.log('🔍 messageParserOrchestrator: jsonData keys:', Object.keys(jsonData));
        
        const result = await parseJSONMessage(jsonData, parseOptions);
        logger.log('🔍 messageParserOrchestrator: parseJSONMessage returned', result.length, 'parts (sessionId:', capturedSessionId, ')');
        
        if (result.length > 0) {
          logger.log('✅ messageParserOrchestrator: Returning', result.length, 'parts from parseJSONMessage');
          return result;
        } else {
          console.error('❌ parseJSONMessage returned 0 parts after cleaning duplicates');
          return [{ 
            type: 'text', 
            content: 'I apologize, but I encountered an error displaying this response. Please try asking your question again.' 
          }];
        }
      } catch (cleanError) {
        console.error('❌ Failed to clean and parse JSON with duplicates:', cleanError);
        // Continue to regular parsing attempt below
      }
    }
    
    // Normal parsing path (no duplicates detected)
    const rawJsonData = JSON.parse(jsonContent);
    
    // Normalize all keys to handle PascalCase/camelCase variations from AI
    const jsonData = normalizeAIResponse(rawJsonData);
    
    const result = await parseJSONMessage(jsonData, parseOptions);
    
    if (result.length > 0) {
      return result;
    } else if (parseOptions.skipIntroAndClosing) {
      // When skipIntroAndClosing is true, 0 parts is OK - streaming already showed intro/closing
      logger.log('✅ parseJSONMessage returned 0 parts with skipIntroAndClosing=true - streaming handled display');
      return [];
    } else {
      console.error('❌❌❌ CRITICAL: parseJSONMessage returned 0 parts despite having valid JSON!');
      return [{ 
        type: 'text', 
        content: 'I apologize, but I encountered an error displaying this response. Please try asking your question again.' 
      }];
    }
  } catch (error) {
    console.error('❌ messageParserOrchestrator: JSON.parse failed:', error);
    console.error('❌ Error details:', error instanceof Error ? error.message : String(error));
    console.error('❌ Content that failed to parse (first 200 chars):', trimmedContent.substring(0, 200));
    
    // SECOND: Try to extract JSON from markdown code blocks (only if content suggests it might contain JSON)
    if (content.includes('```json')) {
      const markdownResult = await parseMarkdownJSON(content);
      if (markdownResult.length > 0) {
        return markdownResult;
      }
    }
  }
  
  // THIRD: Check for form submissions FIRST (before formatted content)
  if (content.includes('FormType:') || content.includes('FORM_SUBMISSION:')) {
    const formSubmissionResult = parseFormSubmissions(content);
    if (formSubmissionResult) {
      return [{ type: 'form_submission', content: formSubmissionResult }];
    }
  }
  
  // FOURTH: Check for formatted content with multiple JSON objects (only if content suggests JSON)
  if (content.includes('{') || content.includes('[')) {
    const formattedContentParts = parseFormattedContent(content);
    if (formattedContentParts.length > 0) {
      return formattedContentParts;
    }
  }
  
  // CRITICAL: If content looks like JSON but we couldn't parse it, don't show raw JSON to users
  // This prevents ugly JSON blobs from appearing in the chat
  if (looksLikeJSON) {
    console.error('❌ Content looks like JSON but failed all parsing attempts');
    console.error('❌ This likely means the AI returned malformed JSON or there was an encoding issue');
    
    // Try to extract at least some useful text from the JSON
    try {
      // Look for common fields that might contain displayable text
      const introMatch = content.match(/"Intro":\s*"([^"]+)"/);
      const closingMatch = content.match(/"Closing":\s*"([^"]+)"/);
      
      if (introMatch || closingMatch) {
        const extractedParts: MessagePart[] = [];
        if (introMatch) {
          extractedParts.push({ type: 'text', content: introMatch[1].replace(/\\n/g, '\n') });
        }
        if (closingMatch) {
          extractedParts.push({ type: 'text', content: closingMatch[1].replace(/\\n/g, '\n') });
        }
        logger.log('✅ Extracted', extractedParts.length, 'text parts from malformed JSON');
        return extractedParts;
      }
    } catch (extractError) {
      console.error('❌ Failed to extract text from malformed JSON:', extractError);
    }
    
    // Return a user-friendly error message instead of raw JSON
    return [{ 
      type: 'text', 
      content: 'I apologize, but I encountered an error displaying this response. Please try rephrasing your question, or contact support if the issue persists.' 
    }];
  }
  
  // If not JSON and not formatted content, treat as plain text
  return [{ type: 'text', content: content }];
};